import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, Sun, Moon, Copy, CheckCheck, RefreshCw, FileDown, Sparkles, History } from "lucide-react";
import { api } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { themedHex } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SEV_TEXT = { red: "keep-red text-red-400", yellow: "keep-amber text-yellow-400", info: "text-cyan-400" };

export default function Report() {
  const [period, setPeriod] = useState("fy");
  const [report, setReport] = useState(null);
  const [meeting, setMeeting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = async (p = period) => {
    setLoading(true);
    try {
      const { data } = await api.get("/report", { params: { period: p } });
      setReport(data);
      api.get("/insights/history", { params: { period: p } }).then((r) => setHistory(r.data)).catch(() => {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (meeting) document.body.classList.add("print-mode");
    else document.body.classList.remove("print-mode");
    return () => document.body.classList.remove("print-mode");
  }, [meeting]);

  const changePeriod = (p) => { setPeriod(p); load(p); };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/report/export.${fmt}`, { params: { period }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `glh_pre_idt_${period}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
  };

  if (!report) return <div className="p-6 font-mono text-sm text-muted-foreground">Generating report…</div>;

  const m = report.section4_metrics;
  const aq = report.section2_action_queue;

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="report-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head font-black text-3xl tracking-tight">Pre-IDT Report</h1>
          <p className="text-sm text-muted-foreground font-mono mt-1">Generated {report.generated_at} · {report.fiscal_week} · <span className="text-emerald-400">100% deterministic — no PHI to any model</span></p>
        </div>
        <div className="flex items-center gap-2 no-print">
          <Select value={period} onValueChange={changePeriod}>
            <SelectTrigger className="w-[150px] bg-slate-900/60 border-white/10 h-9 text-sm" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="fy">This Fiscal Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => load()} className="gap-2 border-white/15 h-9" data-testid="refresh-report"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
          <Button variant="outline" onClick={() => setMeeting((v) => !v)} className="gap-2 border-white/15 h-9" data-testid="meeting-mode-toggle">
            {meeting ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}{meeting ? "Dark" : "Meeting Mode"}
          </Button>
          <Button variant="outline" onClick={() => download("csv")} className="gap-2 border-white/15 h-9" data-testid="export-csv-btn"><FileDown className="w-4 h-4" /> CSV</Button>
          <Button variant="outline" onClick={() => download("pdf")} className="gap-2 border-white/15 h-9" data-testid="export-pdf-btn"><FileDown className="w-4 h-4" /> PDF</Button>
          <Button onClick={() => window.print()} className="gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold h-9" data-testid="print-btn"><Printer className="w-4 h-4" /> Print</Button>
        </div>
      </div>

      {/* §1 */}
      <Section num="1" title="Data Quality Audit" testid="section-1">
        <div className="glass rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono border-b border-white/8">
              <th className="text-left px-3 py-2">Rule</th><th className="text-left px-2 py-2">ID</th><th className="text-left px-2 py-2">Pt</th><th className="text-left px-2 py-2">Lane</th><th className="text-left px-3 py-2">Detail</th>
            </tr></thead>
            <tbody>
              {report.section1_audit.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-emerald-400 font-mono text-xs">No data-quality issues.</td></tr>}
              {report.section1_audit.map((f, i) => (
                <tr key={i} className="border-b border-white/5 last:border-0">
                  <td className={`px-3 py-2 font-medium ${SEV_TEXT[f.severity] || ""}`}>{f.rule}</td>
                  <td className="px-2 py-2 font-mono text-cyan-300 text-xs">{f.referral_id}</td>
                  <td className="px-2 py-2 font-mono">{f.initials}</td>
                  <td className="px-2 py-2 text-xs">{f.lane}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{f.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* §2 */}
      <Section num="2" title="Action Queue" testid="section-2" hint="recommendations for the meeting — the app never changes status automatically">
        <div className="grid md:grid-cols-3 gap-3">
          <QueueColumn title="RED" color="#EF4444" items={aq.red} pulse />
          <QueueColumn title="YELLOW" color="#EAB308" items={aq.yellow} />
          <QueueColumn title="CLOSE-OUT" color="#8B5CF6" items={aq.closeout} />
        </div>
      </Section>

      {/* §3 */}
      <Section num="3" title="Follow-up Prompts" testid="section-3" hint="Teams / email-ready · initials + ID only">
        <div className="flex justify-end mb-2 no-print">
          <CopyBtn label="Copy all" text={report.section3_prompts.map((p) => p.text).join("\n")} />
        </div>
        <div className="glass rounded-xl divide-y divide-white/5">
          {report.section3_prompts.length === 0 && <div className="px-3 py-6 text-center text-muted-foreground font-mono text-xs">No follow-ups needed.</div>}
          {report.section3_prompts.map((p, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2.5" data-testid={`prompt-${i}`}>
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${p.priority === 0 ? "bg-red-500" : "bg-yellow-500"}`} />
              <code className="flex-1 text-xs md:text-sm text-slate-200">{p.text}</code>
              {p.unassigned && <span className="text-[11px] font-mono text-amber-400 shrink-0 no-print">UNASSIGNED</span>}
              <span className="no-print shrink-0"><CopyBtn text={p.text} compact /></span>
            </div>
          ))}
        </div>
      </Section>

      {/* §4 */}
      <Section num="4" title="Pipeline Metrics" testid="section-4" hint={`${m.period_label} · ${m.range.start || "—"} → ${m.range.end}`}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <Metric label="Completed Rate" value={m.completed_rate == null ? "—" : `${m.completed_rate}%`} sub={`Completed ÷ (Completed+Not) · n=${m.completed_rate_denominator}`} color="#34D399" />
          <Metric label="Median Cycle" value={m.median_cycle_biz == null ? "—" : `${m.median_cycle_biz}d`} sub={`max ${m.max_cycle_biz ?? "—"}d · biz-days`} color="#22D3EE" />
          <Metric label="Unassigned" value={`${m.unassigned_rate}%`} sub="of open" color="#F59E0B" />
          <Metric label="Stale" value={`${m.stale_rate}%`} sub="7d+ no note" color="#64748B" />
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <BreakTable title="By Lane" data={m.by_lane} />
          <BreakTable title="By Source" data={m.by_source} />
          <BreakTable title="Non-completion Reasons" data={m.non_completion_reasons} />
        </div>
        <p className="text-xs text-muted-foreground font-mono mt-2">{m.total_in_period} referrals in period · {m.out_of_summary_count} OUT-OF-SUMMARY excluded from counts.</p>
      </Section>

      {/* §5 */}
      <Section num="5" title="Insights" testid="section-5" hint={report.ai_enabled ? "rules-based + AI phrasing (de-identified)" : "rules-based · each cites a metric"}>
        <div className="glass glass-violet rounded-xl p-4 space-y-3">
          {report.section5_narrative && (
            <div className="mb-1 pb-3 border-b border-white/8" data-testid="ai-narrative">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Sparkles className="w-3.5 h-3.5 text-violet-300" />
                <span className="text-[11px] uppercase tracking-[0.18em] text-violet-300 font-mono">AI summary · de-identified aggregates only</span>
              </div>
              <p className="text-sm text-slate-100 leading-relaxed whitespace-pre-line">{report.section5_narrative}</p>
            </div>
          )}
          {report.section5_insights.length === 0 && <p className="text-muted-foreground font-mono text-xs">No notable patterns this period.</p>}
          {report.section5_insights.map((ins, i) => (
            <div key={i} className="flex gap-3 items-start" data-testid={`insight-${i}`}>
              <span className="font-mono text-2xl font-bold text-violet-400 glow-num tnum leading-none">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <p className="text-sm text-slate-100">{ins.finding}</p>
                <p className="text-xs text-cyan-400/90 font-mono mt-0.5">↳ {ins.metric}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 no-print">
          <button onClick={() => setShowHistory((v) => !v)} className="inline-flex items-center gap-2 text-xs font-mono text-violet-300 hover:text-violet-200" data-testid="toggle-history">
            <History className="w-3.5 h-3.5" /> {showHistory ? "Hide" : "Show"} insight history ({history.length})
          </button>
        </div>
        {showHistory && (
          <div className="mt-2 glass rounded-xl divide-y divide-white/8" data-testid="insight-history">
            {history.length === 0 && <div className="px-4 py-4 text-xs font-mono text-muted-foreground">No prior snapshots yet.</div>}
            {history.map((h, i) => (
              <div key={i} className="px-4 py-3" data-testid={`history-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-xs text-cyan-300">{h.fiscal_week}</span>
                  <span className="font-mono text-[11px] text-muted-foreground">{h.generated_at} · compl {h.completed_rate ?? "—"}% · stale {h.stale_rate ?? "—"}%</span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-line">{h.narrative}</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* §6 */}
      <Section num="6" title="Questions for Administrator" testid="section-6" hint="surfaced from unresolved gaps · capped at 5">
        <div className="glass rounded-xl p-4">
          {report.section6_questions.length === 0 && <p className="text-muted-foreground font-mono text-xs">No open questions.</p>}
          <ol className="space-y-2.5">
            {report.section6_questions.map((qq, i) => (
              <li key={i} className="flex gap-3 text-sm" data-testid={`question-${i}`}>
                <span className="font-mono text-cyan-400 font-bold">Q{i + 1}</span>
                <span className="text-slate-100">{qq}</span>
              </li>
            ))}
          </ol>
        </div>
      </Section>
    </div>
  );
}

function Section({ num, title, hint, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-baseline gap-3 mb-2.5">
        <span className="font-mono text-cyan-400 text-sm font-bold">§{num}</span>
        <h2 className="font-head font-bold text-xl tracking-tight">{title}</h2>
        {hint && <span className="text-xs text-muted-foreground font-mono">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function QueueColumn({ title, color: rawColor, items, pulse }) {
  const { theme } = useTheme();
  const color = themedHex(rawColor, theme);
  return (
    <div className="glass rounded-xl p-3" style={{ borderColor: `${color}55`, boxShadow: `inset 0 0 0 1px ${color}33` }}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-xs font-bold tracking-widest" style={{ color }}>{title}</span>
        <span className="font-mono text-xs tnum" style={{ color }}>{items.length}</span>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-xs text-muted-foreground font-mono py-2">none</p>}
        {items.map((it) => (
          <div key={it.referral_id} className={`rounded-lg border p-2.5 ${pulse ? "pulse-alert" : ""}`} style={{ borderColor: `${color}44`, background: `${color}0d` }} data-testid={`aq-${it.referral_id}`}>
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="text-cyan-300">{it.referral_id} · {it.initials}</span>
              <span className="text-muted-foreground">#{it.rank}</span>
            </div>
            <div className="text-xs text-muted-foreground font-mono mt-0.5">{it.lane} · {it.status}{it.detail ? ` · ${it.detail}` : ""}</div>
            <div className="text-xs mt-1 text-slate-200">{it.recommendation}</div>
            <div className="text-[11px] font-mono mt-1" style={{ color }}>→ {it.owner}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value, sub, color }) {
  const { theme } = useTheme();
  const c = themedHex(color, theme);
  return (
    <div className="glass rounded-xl px-3 py-2.5">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono">{label}</div>
      <div className="font-mono text-2xl font-medium glow-num tnum mt-1" style={{ color: c }}>{value}</div>
      <div className="text-[11px] text-muted-foreground font-mono">{sub}</div>
    </div>
  );
}

function BreakTable({ title, data }) {
  const entries = Object.entries(data || {});
  return (
    <div className="glass rounded-xl p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono mb-2">{title}</div>
      {entries.length === 0 && <p className="text-xs text-muted-foreground font-mono">—</p>}
      <div className="space-y-1">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-xs">
            <span className="truncate mr-2">{k}</span>
            <span className="font-mono tnum text-cyan-300">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CopyBtn({ text, label, compact }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setDone(true); toast.success("Copied"); setTimeout(() => setDone(false), 1500); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <button onClick={copy} className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-cyan-300 transition-colors" data-testid="copy-btn">
      {done ? <CheckCheck className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}{!compact && (label || "Copy")}
    </button>
  );
}
